import React, { useEffect, useRef, useState } from 'react';
import './works.css';

const UberToast = () => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 1800);
    return () => clearTimeout(timer);
  }, []);

  if (dismissed) return null;

  return (
    <div className={`uberToast ${visible ? 'uberToastVisible' : ''}`}>
      <button className="uberToastClose" onClick={() => setDismissed(true)} aria-label="Dismiss">✕</button>
      <div className="uberToastIcon">🤖</div>
      <div className="uberToastBody">
        <span className="uberToastLabel">FEATURED PROJECT</span>
        <p className="uberToastTitle">Agentic Ride-Hailing Assistant</p>
        <p className="uberToastSub">LangChain · RAG · Gemini APIs · 85% faster execution</p>
        <a
          href="https://uber-ai.streamlit.app/"
          target="_blank"
          rel="noreferrer"
          className="uberToastCta"
          onClick={() => setDismissed(true)}
        >
          Check it out →
        </a>
      </div>
    </div>
  );
};

const useReveal = (threshold = 0.12) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const projects = [
  {
    id: 1,
    title: "Airbnb Analytics Pipeline",
    description: "ELT pipeline from AWS S3 into Snowflake using dbt Core. Implements Medallion Architecture with Jinja macros, schema tests, and SCD Type 2 snapshots. Live Streamlit dashboard surfacing pricing, occupancy, and host KPIs.",
    techStack: ["AWS S3", "Snowflake", "dbt Core", "Streamlit", "Python", "SQL"],
    metrics: [{ label: "Architecture", value: "Medallion" }, { label: "History", value: "SCD Type 2" }, { label: "Dashboard", value: "Live" }],
    github: "https://github.com/avikalsingh/AirBnb_Data_Engineer_Project_with_Snowflake_DBT",
    liveDemo: "https://airbnbwithdbt.streamlit.app/",
    category: "Data Engineering", emoji: "🏠"
  },
  {
    id: 2,
    title: "Multi-Task Chest X-Ray Analysis",
    description: "Unified deep learning system performing simultaneous radiology report generation (FINDINGS + IMPRESSION) and 14-label disease classification on 119,533 MIMIC-CXR records. Frozen BiomedCLIP ViT-B/16 encoder paired with a learned ImageAdaptiveVisualAbstractor for token compression; Meditron-7B decoder with 4-bit NF4 quantization and LoRA — training only 73M of 3.6B parameters on 6× NVIDIA A6000 GPUs. SPIE Medical Imaging accepted.",
    techStack: ["PyTorch", "BiomedCLIP", "Meditron-7B", "PEFT/LoRA", "Hugging Face", "CUDA", "scikit-learn"],
    metrics: [{ label: "micro-AUROC", value: "0.830" }, { label: "METEOR", value: "0.233" }, { label: "Params", value: "73M/3.6B" }],
    github: "https://github.com/avikalsingh/Multi-Task-X-Ray-Analysis",
    liveDemo: null, category: "Healthcare AI", emoji: "🩺"
  },
  {
    id: 3,
    title: "Agentic Ride-Hailing Analytics Assistant",
    description: "Agentic AI analytics assistant using Gemini APIs, LangChain-style orchestration, RAG, tool calling, MCP-style interfaces, structured JSON outputs, and prompt validation to answer natural-language questions on pricing, demand, and city-level ride patterns. Self-healing pipeline checks handle schema drift and failed transformations. Powered by Azure EventHub/Kafka, Databricks Delta Live Tables, Delta Lake, Unity Catalog, Medallion Architecture, Star Schema, and SCD Type 2.",
    techStack: ["Gemini APIs", "LangChain", "RAG", "Azure EventHub", "Databricks", "Delta Lake", "Spark/PySpark", "Streamlit"],
    metrics: [{ label: "Exec Time", value: "85% ↓" }, { label: "Architecture", value: "Medallion" }, { label: "Schema", value: "Star" }],
    github: "https://github.com/avikalsingh/uber-ai",
    liveDemo: "https://uber-ai.streamlit.app/", category: "Agentic AI", featured: true, emoji: "🤖"
  },
  {
    id: 4,
    title: "Tweet Analytics for Disaster",
    description: "Deployed NLP pipeline for disaster tweet classification to aid emergency response.",
    techStack: ["Python", "TensorFlow", "Tweepy", "Streamlit"],
    metrics: [{ label: "Accuracy", value: "94%" }, { label: "Latency", value: "< 200ms" }],
    github: "https://github.com/avikalsingh/Tweet-Analytics-for-Disaster-Calamity-Management",
    liveDemo: "https://tweet-analytics-for-disaster-calamity-management.streamlit.app/",
    category: "NLP", emoji: "📊"
  },
  {
    id: 4,
    title: "Digital HR Management System",
    description: "Full-stack enterprise HR solution with employee management, payroll automation, and analytics dashboard.",
    techStack: ["React", "Spring Boot", "PostgreSQL", "AWS"],
    metrics: [{ label: "Users", value: "500+" }, { label: "Uptime", value: "99.8%" }],
    github: "https://github.com/avikalsingh/Digital-HR/",
    liveDemo: "https://avikalsingh.pythonanywhere.com/",
    category: "Full-Stack", emoji: "💼"
  },
  // {
  //   id: 6,
  //   title: "Wine Quality Prediction",
  //   description: "ML model predicting wine quality from physicochemical properties with explainable AI insights using SHAP values.",
  //   techStack: ["Scikit-learn", "Pandas", "SHAP", "Streamlit"],
  //   metrics: [{ label: "Accuracy", value: "92%" }, { label: "Features", value: "11" }],
  //   github: "https://github.com/avikalsingh/Predictive-Analysis-on-Wine-Quality-Assessment-/",
  //   liveDemo: null, category: "Data Science", emoji: "🍷"
  // },
  // {
  //   id: 7,
  //   title: "Personal Portfolio Website",
  //   description: "Modern, responsive portfolio built with React featuring smooth animations, contact forms, and reCAPTCHA integration.",
  //   techStack: ["React", "EmailJS", "Vercel", "CSS3"],
  //   metrics: [{ label: "Lighthouse", value: "95+" }, { label: "Load Time", value: "< 2s" }],
  //   github: "https://github.com/avikalsingh/portfolio",
  //   liveDemo: "https://avikalsingh.vercel.app/",
  //   category: "Web Dev", emoji: "🌐"
  // }
];

const CardLinks = ({ project }) => (
  <div className="projectLinks">
    {project.github && (
      <a href={project.github} target="_blank" rel="noreferrer" className="projectLink github">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
        </svg>
        GitHub
      </a>
    )}
    {project.liveDemo && (
      <a href={project.liveDemo} target="_blank" rel="noreferrer" className="projectLink demo">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
          <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
        </svg>
        Live Demo
      </a>
    )}
  </div>
);

const ProjectCard = ({ project, index }) => {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={`projectCard ${visible ? 'visible' : ''}`}
      style={{ animationDelay: `${(index % 2) * 0.1}s` }}
    >
      <div className="projectCategory">{project.category}</div>
      <div className="projectContent">
        <h3 className="projectTitle"><span className="projectEmoji">{project.emoji}</span>{project.title}</h3>
        <p className="projectDesc">{project.description}</p>
        <div className="projectTechStack">
          {project.techStack.map((tech, i) => <span key={i} className="techBadge">{tech}</span>)}
        </div>
        <div className="projectMetrics">
          {project.metrics.map((metric, i) => (
            <div key={i} className="metric">
              <span className="metricValue">{metric.value}</span>
              <span className="metricLabel">{metric.label}</span>
            </div>
          ))}
        </div>
      </div>
      <CardLinks project={project} />
    </div>
  );
};

const Works = () => {
  const [headerRef, headerVisible] = useReveal(0.2);
  const featured = projects.find(p => p.featured);
  const [featuredRef, featuredVisible] = useReveal();
  const rest = projects.filter(p => !p.featured);

  return (
    <>
    <UberToast />
    <section id="works">
      <div className="worksContainer">
        <div ref={headerRef} className={`worksHeader ${headerVisible ? 'visible' : ''}`}>
          <span className="worksLabel">PROJECTS</span>
          <h2 className="worksTitle">What I've Built</h2>
          <span className="worksDesc">
            Agentic AI, multimodal ML, and data infrastructure —
            each project built for real-world scale with quantified impact.
          </span>
        </div>

        <div className="projectsGrid">
          <div
            ref={featuredRef}
            className={`projectCard featuredCard ${featuredVisible ? 'visible' : ''}`}
          >
            <div className="featuredBanner"><span>⭐ Featured Project</span></div>
            <div className="featuredLeft">
              <span className="projectCategory">{featured.category}</span>
              <div className="projectContent">
                <h3 className="projectTitle"><span className="projectEmoji">{featured.emoji}</span>{featured.title}</h3>
                <p className="projectDesc">{featured.description}</p>
                <div className="projectTechStack">
                  {featured.techStack.map((tech, i) => <span key={i} className="techBadge">{tech}</span>)}
                </div>
              </div>
            </div>
            <div className="featuredRight">
              <div className="projectMetrics">
                {featured.metrics.map((metric, i) => (
                  <div key={i} className="metric">
                    <span className="metricValue">{metric.value}</span>
                    <span className="metricLabel">{metric.label}</span>
                  </div>
                ))}
              </div>
              <CardLinks project={featured} />
            </div>
          </div>

          {rest.map((project, i) => (
            <ProjectCard key={project.id} project={project} index={i} />
          ))}
        </div>

        <a href="https://github.com/avikalsingh" target="_blank" rel="noreferrer" className="workBtn">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
          </svg>
          $ git browse --all-projects
        </a>
      </div>
    </section>
    </>
  );
};

export default Works;